import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Storage from '../utils/storageES.js';
import LanguageSwitcher from './LanguageSwitcher.jsx';
import HomeButton from './HomeButton.jsx';
import BackupSettings from './BackupSettings.jsx';

export default function Settings({ userData, onNavigate }) {
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState({
    language: i18n.language || 'en',
    userName: '',
    userNationality: '',
    theme: 'auto'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    const savedSettings = Storage.getSettings();
    setSettings({
      language: i18n.language || 'en',
      userName: savedSettings.userName || '',
      userNationality: savedSettings.userNationality || '',
      theme: savedSettings.theme || 'auto'
    });
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f1eb 0%, #ede4d3 100%)',
      padding: '20px',
      position: 'relative'
    }}>
      {/* Home Button */}
      <HomeButton onNavigate={onNavigate} />

      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingTop: '60px'
      }}>
        <h1 style={{
          fontSize: '36px',
          fontWeight: '900',
          color: '#8B7355',
          margin: '0 0 8px 0',
          letterSpacing: '2px',
          fontFamily: "'Noto Serif', serif",
          textTransform: 'uppercase'
        }}>
          {t('settings.title')}
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#8B7355',
          margin: 0,
          opacity: 0.8
        }}>
          {t('settings.subtitle')}
        </p>
      </div>

      {/* User Information — removed (read-only & redundant; trip shows on Home) */}
      <div style={{ display: 'none' }}>
        <h2>
          {t('settings.userInfo')}
        </h2>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '700',
            color: '#B8A082',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {t('settings.userName')}
          </label>
          <div style={{
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #FAF8F5 0%, #F9F6F2 100%)',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#8B7355',
            fontWeight: '600',
            border: '1px solid rgba(184, 160, 130, 0.2)'
          }}>
            {settings.userName || t('status.notSet')}
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '700',
            color: '#B8A082',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {t('settings.nationality')}
          </label>
          <div style={{
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #FAF8F5 0%, #F9F6F2 100%)',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#8B7355',
            fontWeight: '600',
            border: '1px solid rgba(184, 160, 130, 0.2)'
          }}>
            {settings.userNationality || t('status.notSet')}
          </div>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '700',
            color: '#B8A082',
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            {t('settings.currentTrip')}
          </label>
          <div style={{
            padding: '14px 18px',
            background: 'linear-gradient(135deg, #FAF8F5 0%, #F9F6F2 100%)',
            borderRadius: '12px',
            fontSize: '15px',
            color: '#8B7355',
            fontWeight: '600',
            border: '1px solid rgba(184, 160, 130, 0.2)'
          }}>
            {userData?.destination && userData?.country
              ? `${userData.destination}, ${userData.country}`
              : t('status.noActiveTrip')}
          </div>
        </div>
      </div>

      {/* Language Settings */}
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '24px',
        marginBottom: '16px',
        boxShadow: '0 4px 12px rgba(139, 115, 85, 0.1)',
        border: '1px solid rgba(184, 160, 130, 0.2)'
      }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#8B7355',
          margin: '0 0 12px 0',
          letterSpacing: '-0.5px'
        }}>
          {t('settings.languageSettings')}
        </h2>
        <p style={{
          fontSize: '14px',
          color: '#B8A082',
          margin: '0 0 20px 0',
          lineHeight: '1.6'
        }}>
          {t('settings.languageDescription')}
        </p>

        <LanguageSwitcher />

        <p style={{
          fontSize: '12px',
          color: '#B8A082',
          margin: '16px 0 0 0',
          fontStyle: 'italic',
          opacity: 0.8
        }}>
          {t('settings.languageSaved')}
        </p>
      </div>

      {/* Backup Settings */}
      <div style={{ marginBottom: '16px' }}>
        <BackupSettings />
      </div>

      {/* App Information — slimmed to the footer below */}
      <div style={{ display: 'none' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#8B7355',
          margin: '0 0 20px 0',
          letterSpacing: '-0.5px'
        }}>
          {t('settings.appInfo')}
        </h2>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 0',
          borderBottom: '1px solid rgba(184, 160, 130, 0.2)'
        }}>
          <span style={{ fontSize: '14px', color: '#B8A082', fontWeight: '600' }}>{t('settings.version')}</span>
          <span style={{ fontSize: '15px', color: '#8B7355', fontWeight: '700' }}>{t('app.version')}</span>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 0',
          borderBottom: '1px solid rgba(184, 160, 130, 0.2)'
        }}>
          <span style={{ fontSize: '14px', color: '#B8A082', fontWeight: '600' }}>{t('settings.appName')}</span>
          <span style={{ fontSize: '15px', color: '#8B7355', fontWeight: '700' }}>{t('app.name')}</span>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 0'
        }}>
          <span style={{ fontSize: '14px', color: '#B8A082', fontWeight: '600' }}>{t('settings.by')}</span>
          <span style={{ fontSize: '15px', color: '#8B7355', fontWeight: '700' }}>LOS DIAS</span>
        </div>
      </div>

      {/* Footer — version + legal links */}
      <div style={{ textAlign: 'center', padding: '8px 0 90px', color: '#B8A082', fontSize: '13px', lineHeight: 1.9 }}>
        <div>{t('app.name')} · {t('app.version')} · Los Días</div>
        <div>
          <a href="./privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2D5A6E', marginRight: '14px' }}>{t('settings.privacy')}</a>
          <a href="./terms.html" target="_blank" rel="noopener noreferrer" style={{ color: '#2D5A6E' }}>{t('settings.terms')}</a>
        </div>
      </div>
    </div>
  );
}
